import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Megaphone, Plus, Edit2, Trash2, Eye, EyeOff, Calendar, 
  Clock, Tag, MapPin, AlertTriangle, Gift, Bell, Trophy,
  Settings, ChevronDown, ChevronUp, ExternalLink, Save, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_CONFIG = {
  promotion: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Gift },
  update: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Bell },
  alert: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  tournament: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Trophy },
  system_notice: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Settings }
};

const PRIORITY_CONFIG = {
  normal: { color: 'bg-white/10 text-white/70', label: 'Normal' },
  pinned: { color: 'bg-[#00FF94]/20 text-[#00FF94]', label: 'Pinned' },
  popup: { color: 'bg-[#00F0FF]/20 text-[#00F0FF]', label: 'Popup' }
};

const AnnouncementForm = ({ announcement, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: announcement?.title || '',
    message: announcement?.message || '',
    tag: announcement?.tag || 'update',
    priority: announcement?.priority || 'normal',
    show_homepage: announcement?.show_homepage ?? true,
    show_dashboard: announcement?.show_dashboard ?? true,
    show_lobby: announcement?.show_lobby ?? true,
    show_popup: announcement?.show_popup ?? false,
    start_date: announcement?.start_date ? announcement.start_date.split('T')[0] : '',
    expiration_date: announcement?.expiration_date ? announcement.expiration_date.split('T')[0] : '',
    cta_text: announcement?.cta_text || '',
    cta_link: announcement?.cta_link || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_date: form.start_date ? `${form.start_date}T00:00:00Z` : null,
        expiration_date: form.expiration_date ? `${form.expiration_date}T23:59:59Z` : null
      };
      
      if (announcement?.announcement_id) {
        await axios.put(`${API}/admin/announcements/${announcement.announcement_id}`, payload, { withCredentials: true });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API}/admin/announcements`, payload, { withCredentials: true });
        toast.success('Announcement created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-xl bg-[#1a1a2e] border border-white/10"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#00FF94]" />
          {announcement ? 'Edit Announcement' : 'Create Announcement'}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="text-sm text-white/70 mb-1 block">Title *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Announcement title"
            className="bg-white/5 border-white/20"
          />
        </div>

        {/* Message */}
        <div>
          <label className="text-sm text-white/70 mb-1 block">Message *</label>
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Announcement message..."
            rows={4}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00FF94]/50"
          />
        </div>

        {/* Tag & Priority */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-white/70 mb-1 block">Tag</label>
            <select
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-white focus:outline-none"
            >
              <option value="promotion">Promotion</option>
              <option value="update">Update</option>
              <option value="alert">Alert</option>
              <option value="tournament">Tournament</option>
              <option value="system_notice">System Notice</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-white/70 mb-1 block">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-white focus:outline-none"
            >
              <option value="normal">Normal</option>
              <option value="pinned">Pinned (Always on top)</option>
              <option value="popup">Popup (Show once per session)</option>
            </select>
          </div>
        </div>

        {/* Display Locations */}
        <div>
          <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Display Locations
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'show_homepage', label: 'Homepage Banner' },
              { key: 'show_dashboard', label: 'Dashboard' },
              { key: 'show_lobby', label: 'Match Lobby' },
              { key: 'show_popup', label: 'Popup Modal' }
            ].map(loc => (
              <label key={loc.key} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors">
                <input
                  type="checkbox"
                  checked={form[loc.key]}
                  onChange={(e) => setForm({ ...form, [loc.key]: e.target.checked })}
                  className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#00FF94] focus:ring-[#00FF94]"
                />
                <span className="text-sm">{loc.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Scheduling */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-white/70 mb-1 block flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Start Date (Optional)
            </label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div>
            <label className="text-sm text-white/70 mb-1 block flex items-center gap-2">
              <Clock className="w-4 h-4" /> Expiration Date (Optional)
            </label>
            <Input
              type="date"
              value={form.expiration_date}
              onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
        </div>

        {/* CTA Button */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-white/70 mb-1 block">CTA Button Text (Optional)</label>
            <Input
              value={form.cta_text}
              onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
              placeholder="e.g., Play Now"
              className="bg-white/5 border-white/20"
            />
          </div>
          <div>
            <label className="text-sm text-white/70 mb-1 block">CTA Button Link (Optional)</label>
            <Input
              value={form.cta_link}
              onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
              placeholder="e.g., /lobby"
              className="bg-white/5 border-white/20"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button type="submit" className="btn-primary flex-1" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Announcement'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="btn-secondary">
            Cancel
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

const AnnouncementCard = ({ announcement, onEdit, onToggle, onDelete }) => {
  const TagIcon = TAG_CONFIG[announcement.tag]?.icon || Bell;
  const tagColor = TAG_CONFIG[announcement.tag]?.color || TAG_CONFIG.update.color;
  const priorityConfig = PRIORITY_CONFIG[announcement.priority] || PRIORITY_CONFIG.normal;
  const [expanded, setExpanded] = useState(false);

  const locations = [
    { key: 'show_homepage', label: 'Homepage' },
    { key: 'show_dashboard', label: 'Dashboard' },
    { key: 'show_lobby', label: 'Lobby' },
    { key: 'show_popup', label: 'Popup' }
  ].filter(loc => announcement[loc.key]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border transition-all ${
        announcement.currently_active 
          ? 'bg-white/5 border-[#00FF94]/30' 
          : 'bg-white/[0.02] border-white/10 opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tagColor} flex items-center gap-1`}>
              <TagIcon className="w-3 h-3" />
              {announcement.tag.replace('_', ' ')}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig.color}`}>
              {priorityConfig.label}
            </span>
            {announcement.currently_active ? (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                Active
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                Inactive
              </span>
            )}
          </div>
          
          <h4 className="font-semibold text-white mb-1">{announcement.title}</h4>
          
          <p className={`text-white/60 text-sm ${expanded ? '' : 'line-clamp-2'}`}>
            {announcement.message}
          </p>
          
          {announcement.message.length > 100 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-[#00F0FF] text-xs mt-1 hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}

          {/* Locations */}
          <div className="flex flex-wrap gap-1 mt-3">
            {locations.map(loc => (
              <span key={loc.key} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">
                {loc.label}
              </span>
            ))}
          </div>

          {/* Dates */}
          <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
            {announcement.start_date && (
              <span>Starts: {new Date(announcement.start_date).toLocaleDateString()}</span>
            )}
            {announcement.expiration_date && (
              <span>Expires: {new Date(announcement.expiration_date).toLocaleDateString()}</span>
            )}
          </div>

          {/* CTA Preview */}
          {announcement.cta_text && (
            <div className="flex items-center gap-2 mt-2 text-xs text-[#00FF94]">
              <ExternalLink className="w-3 h-3" />
              <span>{announcement.cta_text} → {announcement.cta_link}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => onToggle(announcement.announcement_id)}
            className={announcement.is_active ? 'text-green-400' : 'text-white/40'}
          >
            {announcement.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(announcement)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-red-400 hover:text-red-300"
            onClick={() => onDelete(announcement.announcement_id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleToggle = async (id) => {
    try {
      await axios.post(`${API}/admin/announcements/${id}/toggle`, {}, { withCredentials: true });
      fetchAnnouncements();
      toast.success('Announcement toggled');
    } catch (error) {
      toast.error('Failed to toggle announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true });
      fetchAnnouncements();
      toast.success('Announcement deleted');
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setShowForm(true);
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingAnnouncement(null);
    fetchAnnouncements();
  };

  const activeCount = announcements.filter(a => a.currently_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6 text-[#00FF94]" />
          <div>
            <h2 className="font-heading font-semibold text-xl">Announcement Board</h2>
            <p className="text-white/50 text-sm">{activeCount} active announcements</p>
          </div>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            New Announcement
          </Button>
        )}
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <AnnouncementForm
            announcement={editingAnnouncement}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setEditingAnnouncement(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Announcements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 text-white/50">
          <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No announcements yet</p>
          <p className="text-sm">Create your first announcement to engage users</p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <AnnouncementCard
              key={announcement.announcement_id}
              announcement={announcement}
              onEdit={handleEdit}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
